import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import storage from '../platform/storage';
import events from '../platform/events';

// Create context
const UserContext = createContext();

// User data keys for storage
const USER_DATA_KEYS = {
  NICKNAME: 'user_nickname',
  COUNTRY_CODE: 'user_country_code',
  LANGUAGE_CODE: 'user_language_code',
  THEME: 'user_theme', // Backend theme palette (summer, winter, etc.)
  LIGHT_MODE: 'user_light_mode', // Backend color mode (light, dark)
};

export const UserProvider = ({ children }) => {
  // Filled from storage by the hydration effect below; App.js holds rendering
  // until `hydrated` is true
  const [userData, setUserData] = useState({
    nickname: null,
    countryCode: null,
    languageCode: null,
    theme: null,
    lightMode: null,
  });
  const [hydrated, setHydrated] = useState(false);
  
  // Load user data from storage on component mount
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      // Migration: Handle old storage keys (one-time cleanup)
      const oldColorPalette = await storage.getItem('user_color_palette');
      const oldColorMode = await storage.getItem('user_color_mode');
      if (oldColorPalette) {
        await storage.setItem(USER_DATA_KEYS.THEME, oldColorPalette);
        await storage.removeItem('user_color_palette');
      }
      if (oldColorMode) {
        await storage.setItem(USER_DATA_KEYS.LIGHT_MODE, oldColorMode);
        await storage.removeItem('user_color_mode');
      }

      const storedNickname = await storage.getItem(USER_DATA_KEYS.NICKNAME);
      const storedCountryCode = await storage.getItem(USER_DATA_KEYS.COUNTRY_CODE);
      const storedLanguageCode = (await storage.getItem(USER_DATA_KEYS.LANGUAGE_CODE)) || 'en';
      const storedTheme = await storage.getItem(USER_DATA_KEYS.THEME);
      const storedLightMode = await storage.getItem(USER_DATA_KEYS.LIGHT_MODE);

      if (cancelled) return;

      // Values that arrived through userDataUpdated while loading are newer
      setUserData(prevData => ({
        ...prevData,
        nickname: prevData.nickname || storedNickname,
        countryCode: prevData.countryCode || storedCountryCode,
        languageCode: prevData.languageCode || storedLanguageCode,
        theme: prevData.theme || storedTheme,
        lightMode: prevData.lightMode || storedLightMode
      }));
      setHydrated(true);
    };

    hydrate();
    return () => { cancelled = true; };
  }, []);

  // Function to update user data
  const updateUserData = useCallback((newData) => {
    const updatedData = { ...userData, ...newData };
    
    // Update state
    setUserData(updatedData);
    
    // Update storage
    if (newData.nickname) {
      storage.setItem(USER_DATA_KEYS.NICKNAME, newData.nickname);
    }
    if (newData.countryCode) {
      storage.setItem(USER_DATA_KEYS.COUNTRY_CODE, newData.countryCode);
    }
    if (newData.languageCode) {
      storage.setItem(USER_DATA_KEYS.LANGUAGE_CODE, newData.languageCode);
    }
    if (newData.theme) {
      storage.setItem(USER_DATA_KEYS.THEME, newData.theme);
    }
    if (newData.lightMode) {
      storage.setItem(USER_DATA_KEYS.LIGHT_MODE, newData.lightMode);
    }
    
    // Emit event for other components to react to user data changes
    events.emit('userDataUpdated', updatedData);
  }, [userData]);

  // Function to clear user data (for logout)
  const clearUserData = () => {
    // Clear state
    setUserData({
      nickname: null,
      countryCode: null,
      languageCode: 'en',
      theme: null,
      lightMode: null,
    });
    
    // Clear storage
    storage.removeItem(USER_DATA_KEYS.NICKNAME);
    storage.removeItem(USER_DATA_KEYS.COUNTRY_CODE);
    storage.removeItem(USER_DATA_KEYS.LANGUAGE_CODE);
    storage.removeItem(USER_DATA_KEYS.THEME);
    storage.removeItem(USER_DATA_KEYS.LIGHT_MODE);
    
    // Emit event
    events.emit('userDataCleared');
  };

  // Listen for auth events from other parts of the app
  useEffect(() => {
    const handleAuthEvent = (detail) => {
      if (detail && detail.type === 'logout') {
        clearUserData();
      }
    };

    const handleUserDataUpdated = (detail) => {
      if (detail) {
        const newData = {
          nickname: detail.nickname,
          countryCode: detail.countryCode,
          languageCode: detail.languageCode,
          theme: detail.theme,
          lightMode: detail.lightMode
        };
        // Update state directly without dispatching another event to avoid infinite recursion
        setUserData(prev => ({ ...prev, ...newData }));
        // Update storage
        if (newData.nickname) storage.setItem(USER_DATA_KEYS.NICKNAME, newData.nickname);
        if (newData.countryCode) storage.setItem(USER_DATA_KEYS.COUNTRY_CODE, newData.countryCode);
        if (newData.languageCode) storage.setItem(USER_DATA_KEYS.LANGUAGE_CODE, newData.languageCode);
        if (newData.theme) storage.setItem(USER_DATA_KEYS.THEME, newData.theme);
        if (newData.lightMode) storage.setItem(USER_DATA_KEYS.LIGHT_MODE, newData.lightMode);
      }
    };

    const offAuthEvent = events.on('authEvent', handleAuthEvent);
    const offUserDataUpdated = events.on('userDataUpdated', handleUserDataUpdated);
    
    return () => {
      offAuthEvent();
      offUserDataUpdated();
    };
  }, [updateUserData]);

  return (
    <UserContext.Provider
      value={{
        userData,
        hydrated,
        updateUserData,
        clearUserData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
